import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  Timeline as AreaChartIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

// Chart color scheme
const CHART_COLORS = {
  impressions: '#1976d2',
  engagement: '#4caf50',
  clicks: '#ff9800',
  reach: '#9c27b0',
  gradient: {
    impressions: ['#1976d2', '#42a5f5'],
    engagement: ['#4caf50', '#81c784'],
    clicks: ['#ff9800', '#ffb74d'],
    reach: ['#9c27b0', '#ba68c8']
  }
};

/**
 * Custom tooltip component for charts
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const date = dayjs(label).format('MMM D, YYYY');
    
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 2
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {date}
        </Typography>
        {payload.map((entry) => (
          <Box key={entry.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: entry.color,
                borderRadius: '50%'
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {entry.name}:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

/**
 * Performance Timeline Chart Component
 * 
 * Displays performance metrics over time with multiple visualization options
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Timeline data array
 * @param {boolean} props.loading - Loading state
 * @param {string} props.chartType - Type of chart ('line', 'area', 'composed')
 * @param {Array} props.visibleMetrics - Array of visible metric keys
 * @param {number} props.height - Chart height in pixels
 */
const PerformanceTimelineChart = ({
  data = [],
  loading = false,
  chartType: initialChartType = 'line',
  visibleMetrics: initialVisibleMetrics = ['impressions', 'engagement', 'clicks', 'reach'],
  height = 400
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [chartType, setChartType] = React.useState(initialChartType);
  const [visibleMetrics, setVisibleMetrics] = React.useState(initialVisibleMetrics);

  // Process and format data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map(item => ({
      ...item,
      date: dayjs(item.date).format('MMM D'),
      fullDate: item.date
    }));
  }, [data]);

  // Calculate trend indicators
  const trendData = useMemo(() => {
    if (chartData.length < 2) return {};
    
    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    
    const trends = {};
    visibleMetrics.forEach(metric => {
      const change = ((last[metric] - first[metric]) / first[metric]) * 100;
      trends[metric] = {
        change: change.toFixed(1),
        isPositive: change > 0,
        isFlat: Math.abs(change) < 0.1
      };
    });
    
    return trends;
  }, [chartData, visibleMetrics]);

  /**
   * Handle chart type change
   */
  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  /**
   * Handle metric visibility toggle
   */
  const handleMetricToggle = (event, newMetrics) => {
    if (newMetrics.length > 0) {
      setVisibleMetrics(newMetrics);
    }
  };

  /**
   * Render metric toggle buttons
   */
  const renderMetricToggles = () => {
    const metrics = [
      { key: 'impressions', label: 'Impressions', color: CHART_COLORS.impressions },
      { key: 'engagement', label: 'Engagement', color: CHART_COLORS.engagement },
      { key: 'clicks', label: 'Clicks', color: CHART_COLORS.clicks },
      { key: 'reach', label: 'Reach', color: CHART_COLORS.reach }
    ];

    return (
      <ToggleButtonGroup
        value={visibleMetrics}
        onChange={handleMetricToggle}
        size="small"
        sx={{ flexWrap: 'wrap', gap: 0.5 }}
      >
        {metrics.map((metric) => (
          <ToggleButton
            key={metric.key}
            value={metric.key}
            sx={{
              '&.Mui-selected': {
                bgcolor: metric.color,
                color: 'white',
                '&:hover': {
                  bgcolor: metric.color,
                  opacity: 0.9
                }
              }
            }}
          >
            {metric.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  /**
   * Render chart based on selected type
   */
  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              {visibleMetrics.map((metric) => (
                <linearGradient key={metric} id={`gradient-${metric}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS[metric]} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={CHART_COLORS[metric]} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            {visibleMetrics.map((metric) => (
              <Area
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={CHART_COLORS[metric]}
                fill={`url(#gradient-${metric})`}
                strokeWidth={2}
                name={metric.charAt(0).toUpperCase() + metric.slice(1)}
              />
            ))}
          </AreaChart>
        );
      
      case 'composed':
        return (
          <ComposedChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            {visibleMetrics.includes('impressions') && (
              <Bar
                dataKey="impressions"
                fill={CHART_COLORS.impressions}
                name="Impressions"
                opacity={0.7}
              />
            )}
            {visibleMetrics.filter(m => m !== 'impressions').map((metric) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={CHART_COLORS[metric]}
                strokeWidth={2}
                name={metric.charAt(0).toUpperCase() + metric.slice(1)}
                dot={{ r: 4 }}
              />
            ))}
          </ComposedChart>
        );
      
      default: // line chart
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            {visibleMetrics.map((metric) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={CHART_COLORS[metric]}
                strokeWidth={2}
                name={metric.charAt(0).toUpperCase() + metric.slice(1)}
                dot={{ r: 4 }}
                activeDot={{ r: 6, stroke: CHART_COLORS[metric], strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height, textAlign: 'center' }}>
        <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No performance data available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Data will appear here once you have published posts
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Chart Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        {/* Chart Type Selector */}
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
        >
          <ToggleButton value="line">
            <Tooltip title="Line Chart">
              <LineChartIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="area">
            <Tooltip title="Area Chart">
              <AreaChartIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="composed">
            <Tooltip title="Combined Chart">
              <BarChartIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Metric Toggles */}
        {!isMobile && renderMetricToggles()}
      </Box>

      {/* Mobile Metric Toggles */}
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          {renderMetricToggles()}
        </Box>
      )}

      {/* Chart Container */}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>

      {/* Trend Summary */}
      {Object.keys(trendData).length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Period Trends:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {visibleMetrics.map((metric) => {
              const trend = trendData[metric];
              if (!trend) return null;
              
              return (
                <Box key={metric} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                    {metric}:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: trend.isFlat ? 'text.secondary' : trend.isPositive ? 'success.main' : 'error.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {trend.isPositive ? '+' : ''}{trend.change}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PerformanceTimelineChart;